import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import { usePermissions } from '../hooks/usePermissions';
import styles from '../assets/paymentsWithoutDueDate.module.css';

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const PaymentsWithoutDueDate = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [sortField, setSortField] = useState("amount");
  const [sortDirection, setSortDirection] = useState("desc");
  const [totalRecords, setTotalRecords] = useState(0);
  const [draw, setDraw] = useState(1);
  const drawRef = useRef(1);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef(null);
  const wasInputFocusedRef = useRef(false);
  const isFirstRender = useRef(true);

  // Check if user has payments.manage permission
  const hasPaymentManagePermission = hasPermission('payments.manage');

  // Pagination calculations for server-side
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;

  // Map frontend sort field to backend column index (matching Laravel: Amount, Status, Amount Paid, To Pay)
  const getSortColumnIndex = (field) => {
    const mapping = {
      "amount": 0,        // Amount column
      "status": 1,       // Status column
      "amountPaid": 2,   // Amount Paid column
      "toPay": 3         // To Pay column (outstanding_amount)
    };
    return mapping[field] || 0;
  };

  // Fetch payments with server-side DataTables pagination (same pattern as Users.jsx)
  const loadPaymentsData = () => {
    console.log("🔍 [FRONTEND] Fetching payments without due date with server-side pagination");
    setLoading(true);

    const calculatedStartIndex = (currentPage - 1) * pageSize;
    const datatableParams = {
      draw: drawRef.current,
      start: calculatedStartIndex,
      length: pageSize,
      search: { value: search },
      order: [{ column: getSortColumnIndex(sortField), dir: sortDirection }]
    };

    console.log("📊 [FRONTEND] DataTables params:", datatableParams);

    fetch(`${process.env.REACT_APP_API_URL}/api/payments/without-due-date/datatable`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify(datatableParams)
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("📊 [FRONTEND] DataTables response:", data);
        setPayments(data.data || []);
        setTotalRecords(data.recordsFiltered || 0);
        // Update draw ref and state if response has draw value
        if (data.draw) {
          drawRef.current = data.draw;
          setDraw(data.draw);
        }
        setLoading(false);

        // Only show success toast if there's data, otherwise it's expected to be empty
        if (data.data && data.data.length > 0) {
          console.log(`✅ Loaded ${data.data.length} payments without due date`);
        } else {
          console.log("ℹ️ No payments without due date found (expected - list should be empty)");
        }
      })
      .catch((error) => {
        console.error("❌ [FRONTEND] Error fetching payments:", error);
        setPayments([]);
        setTotalRecords(0);
        setLoading(false);
        toast.error("Failed to load payments without due date data. Please try again.");
      });
  };

  // Fetch payments when dependencies change (same pattern as Users.jsx)
  useEffect(() => {
    loadPaymentsData();
  }, [currentPage, pageSize, search, sortField, sortDirection]);

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
    drawRef.current += 1;
    setDraw(drawRef.current); // Increment draw counter for fresh data
  };

  // Handle search with pagination reset (same as Users.jsx)
  const handleSearchChange = (e) => {
    const value = e.target.value;
    // Track that input is focused before state update
    wasInputFocusedRef.current = document.activeElement === searchInputRef.current;
    setSearch(value);
    setCurrentPage(1); // Reset to first page when searching
  };
  
  // Handle focus events
  const handleSearchFocus = () => {
    wasInputFocusedRef.current = true;
  };
  
  const handleSearchBlur = () => {
    wasInputFocusedRef.current = false;
  };
  
  // Preserve focus on input after rerender (use useLayoutEffect for synchronous execution)
  useLayoutEffect(() => {
    if (wasInputFocusedRef.current && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      const cursorPosition = searchInputRef.current.value.length;
      searchInputRef.current.focus();
      searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  });


  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    drawRef.current += 1;
    setDraw(drawRef.current); // Increment draw counter for fresh sort
  };

  // Render pagination controls (matching Laravel DataTables style)
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    if (currentPage <= halfRange) {
      endPage = Math.min(maxPagesToShow, totalPages);
    }
    if (currentPage + halfRange >= totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(
        <li key={1} className={currentPage === 1 ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(1); }}>
            1
          </a>
        </li>
      );
      if (startPage > 2) {
        pages.push(
          <li key="ellipsis-start" className="disabled">
            <span>...</span>
          </li>
        );
      }
    }

    // Add page numbers in range
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={currentPage === i ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(i); }}>
            {i}
          </a>
        </li>
      );
    }

    // Add ellipsis and last page if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="ellipsis-end" className="disabled">
            <span>...</span>
          </li>
        );
      }
      pages.push(
        <li key={totalPages} className={currentPage === totalPages ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(totalPages); }}>
            {totalPages}
          </a>
        </li>
      );
    }

    return (
      <div className="dataTables_paginate">
        <ul className="pagination">
          <li className={currentPage === 1 ? 'disabled' : ''}>
            <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage > 1) handlePageChange(currentPage - 1); }}>
              Previous
            </a>
          </li>
          {pages}
          <li className={currentPage === totalPages ? 'disabled' : ''}>
            <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) handlePageChange(currentPage + 1); }}>
              Next
            </a>
          </li>
        </ul>
      </div>
    );
  };

  const formatCurrency = (amount) => {
    if (!amount) return '£0';
    return `£${parseFloat(amount).toFixed(0)}`;
  };

  const getPaymentStatusBadge = (status) => {
    if (status === null || status === undefined) {
      return <span className={styles["status-badge"]}>Unknown</span>;
    }

    // Laravel payment_status_reverse mapping (from config/rents.php)
    const statusTextMap = {
      0: 'Not paid',
      1: 'Paid',
      2: 'To pay',
      3: 'Ignore',
      4: 'Payment Plan',
      5: 'Legal',
      6: 'Notice to quit',
    };

    // Map status to CSS class
    const statusClassMap = {
      0: styles["status-badge-not-paid"], // Not paid - danger/orange
      1: styles["status-badge-paid"], // Paid - success/green
      2: styles["status-badge"], // To pay - warning (default teal)
      3: styles["status-badge-no-due-date"], // Ignore - default/gray
      4: styles["status-badge"], // Payment Plan - info (default teal)
      5: styles["status-badge"], // Legal - primary (default teal)
      6: styles["status-badge-overdue"], // Notice to quit - danger/red
    };

    // Handle numeric status (from database)
    const statusNum = typeof status === 'number' ? status : parseInt(status);
    const statusText = statusTextMap[statusNum] || 'Unknown';
    const badgeClass = statusClassMap[statusNum] || styles["status-badge"];

    return <span className={`${styles["status-badge"]} ${badgeClass}`}>{statusText}</span>;
  };

  const handleEditPayment = (payment) => {
    try {
      if (payment.rent_id) {
        navigate(`/rents/${payment.rent_id}/payments/${payment.id}/edit`);
      } else {
        navigate(`/payments/${payment.id}/edit`);
      }
      toast.info("Navigating to payment edit page...");
    } catch (error) {
      console.error("Error navigating to edit:", error);
      toast.error("Failed to navigate to edit page");
    }
  };

  const handleRemovePayment = async (payment) => {
    try {
      const confirmed = window.confirm(
        `Are you sure you want to remove payment ${formatCurrency(payment.amount)}? This action cannot be undone.`
      );

      if (!confirmed) {
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/payments/${payment.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        toast.success("Payment removed successfully!");
        // Reset to first page and increment draw counter for fresh data
        setCurrentPage(1);
        setDraw(prev => prev + 1);
        // loadPaymentsData will be called automatically via useEffect
      } else {
        toast.error("Failed to remove payment");
      }
    } catch (error) {
      console.error("Error removing payment:", error);
      toast.error("Failed to remove payment");
    }
  };

  // Handle Mark as Paid button click
  const handleMarkAsPaid = (payment) => {
    try {
      if (payment.rent_id) {
        navigate(`/rents/${payment.rent_id}/payments/${payment.id}/mark-as-paid`);
      } else {
        toast.error("Cannot mark payment as paid - rent ID missing");
      }
    } catch (error) {
      console.error("Error navigating to mark as paid:", error);
      toast.error("Failed to navigate to mark as paid page");
    }
  };

  // Handle Send Reminder button click
  const handleSendReminder = async (payment) => {
    try {
      const confirmed = window.confirm(
        "Are you sure you want to send the payment reminder email?"
      );

      if (!confirmed) {
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/payments/${payment.id}/reminder`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        toast.success("Payment reminder sent successfully!");
        // Refresh data to show updated status if needed
        setDraw(prev => prev + 1);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to send payment reminder");
      }
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast.error("Failed to send payment reminder");
    }
  };

  // Check if payment is not paid (status = 0 or 'not_paid')
  const isPaymentNotPaid = (payment) => {
    return payment.status === 0 || payment.status === 'not_paid' || payment.status === 'Not paid';
  };

  // Check if payment is overdue (rent has started but payment not paid)
  const isPaymentOverdue = (payment) => {
    if (!isPaymentNotPaid(payment)) {
      return false;
    }
    
    // Check if rent has started
    if (payment.rent?.date_start) {
      const rentStartDate = new Date(payment.rent.date_start);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return rentStartDate <= today;
    }
    
    return false;
  };

  const handleExportCSV = () => {
    const csvContent = "data:text/csv;charset=utf-8,"
      + "Amount,Status,Amount Paid,To Pay\n"
      + payments.map(item => {
          const status = item.status || 'No due date';
          return `"${formatCurrency(item.amount)}","${status}","${formatCurrency(item.amount_paid)}","${formatCurrency(item.amount - item.amount_paid)}"`;
        }).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "payments_without_due_date.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <Loader />;

  return (
    <div className={styles["payments-without-due-date"]}>
      {/* Breadcrumbs */}
      <div className={styles["breadcrumb-row"]}>
        <span className={styles["breadcrumb-main"]}>
          <a href="/dashboard">Dashboard</a>
        </span>
        <span className={styles["breadcrumb-sep"]}>/</span>
        <span className={styles["breadcrumb-current"]}>
          Finance
        </span>
        <span className={styles["breadcrumb-sep"]}>/</span>
        <span className={styles["breadcrumb-main"]}>
          <a href="/payments">Payments</a>
        </span>
        <span className={styles["breadcrumb-sep"]}>/</span>
        <span className={styles["breadcrumb-current"]}>
          Without due date
        </span>
      </div>

      <div className={styles["container-fluid"]}>
        <div className={styles["row"]}>
          <div className={styles["col-xs-12"]}>
            <div className={`${styles["panel"]} ${styles["panel-default"]} ${styles["panel-standard"]}`}>
              <div className={`${styles["panel-heading"]} ${styles["panel-title"]}`}>
                <h3>Payments without due date <span className={styles["text-muted"]}>- the list should be empty!</span></h3>
              </div>

              <div className={styles["panel-body"]}>
                <div className={styles["dataTable-wrapper"]}>
                  {/* Table Controls */}
                  <div className={styles["table-controls"]}>
                    <div className={styles["show-entries"]}>
                      <span>Show</span>
                      <select
                        className={styles["entries-select"]}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="30">30</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                      <span>entries</span>
                    </div>
                    <div className={styles["search-box"]}>
                      <span>Search:</span>
                      <input
                        ref={searchInputRef}
                        type="text"
                        className={styles["search-input"]}
                        placeholder="Search..."
                        value={search}
                        onChange={handleSearchChange}
                        onFocus={handleSearchFocus}
                        onBlur={handleSearchBlur}
                      />
                    </div>
                  </div>

                  <table className={styles["table"]}>
                    <thead>
                      <tr>
                        <th
                          className={styles["sortable"]}
                          onClick={() => handleSort("amount")}
                          style={{ cursor: "pointer" }}
                        >
                          Amount<SortIcon direction={sortField === "amount" ? sortDirection : null} isActive={sortField === "amount"} />
                        </th>
                        <th
                          className={styles["sortable"]}
                          onClick={() => handleSort("status")}
                          style={{ cursor: "pointer" }}
                        >
                          Status<SortIcon direction={sortField === "status" ? sortDirection : null} isActive={sortField === "status"} />
                        </th>
                        <th
                          className={styles["sortable"]}
                          onClick={() => handleSort("amountPaid")}
                          style={{ cursor: "pointer" }}
                        >
                          Amount Paid<SortIcon direction={sortField === "amountPaid" ? sortDirection : null} isActive={sortField === "amountPaid"} />
                        </th>
                        <th
                          className={styles["sortable"]}
                          onClick={() => handleSort("toPay")}
                          style={{ cursor: "pointer" }}
                        >
                          To Pay<SortIcon direction={sortField === "toPay" ? sortDirection : null} isActive={sortField === "toPay"} />
                        </th>
                        <th className={styles["action-head"]}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.length === 0 ? (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                            No payments without due date found - the list should be empty!
                          </td>
                        </tr>
                      ) : (
                        payments.map((payment, index) => (
                          <tr key={payment.id} className={index % 2 === 0 ? styles["even-row"] : styles["odd-row"]}>
                            <td>{formatCurrency(payment.amount)}</td>
                            <td>{getPaymentStatusBadge(payment.status)}</td>
                            <td>{formatCurrency(payment.amount_paid)}</td>
                            <td>{formatCurrency(payment.amount - payment.amount_paid)}</td>
                            <td className={styles["actions-column"]}>
                              {hasPaymentManagePermission && (
                                <div className={styles["action-buttons-group"]}>
                                  {isPaymentNotPaid(payment) && (
                                    <button
                                      className={`${styles["action-btn"]} ${styles["btn-success"]}`}
                                      title="Mark as Paid"
                                      onClick={() => handleMarkAsPaid(payment)}
                                    >
                                      Mark as Paid
                                    </button>
                                  )}
                                  {isPaymentOverdue(payment) && (
                                    <button
                                      className={`${styles["action-btn"]} ${styles["btn-info"]}`}
                                      title="Send Reminder"
                                      onClick={() => handleSendReminder(payment)}
                                    >
                                      Send Reminder
                                    </button>
                                  )}
                                  <button
                                    className={`${styles["action-btn"]} ${styles["btn-edit"]}`}
                                    title="Edit"
                                    onClick={() => handleEditPayment(payment)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className={`${styles["action-btn"]} ${styles["btn-remove"]}`}
                                    title="Remove"
                                    onClick={() => handleRemovePayment(payment)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  {/* Pagination Info and Controls */}
                  <div className="dataTables_info_wrapper" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                    <div className="dataTables_info">
                      Showing {totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{" "}
                      {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} entries
                    </div>
                    {renderPagination()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentsWithoutDueDate;
